// ════════════════════════════════════════════════════════════════════
// src/storeVisits/storeVisitCadence.js   (SVR Phase 3, Sprint 3.2 + 3.4)
//
// ADMIN "Cadence" sub-tab: are we visiting stores often enough?
//   Month view (default):  monthly_target vs visits done that month
//   Week view:             coverage — which stores were visited this week
//                          (no weekly target exists, so this is pure
//                           visited/not-visited coverage, ideal for slides)
//
// Filters (HO/admin): granularity (Month|Week), period picker, Area.
// Excel export of the current view via the SheetJS (XLSX) global that
// admin.html already loads.
//
// Entry point:  window.renderVisitCadence()  → #visit-cadence-root.
// Counts submitted|reviewed visits only. Role-scoped via currentAdmin +
// AREA_STORE_MAP, exactly like the reports/insights modules.
// ════════════════════════════════════════════════════════════════════

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
const C_RED = '#ef4444', C_AMBER = '#f59e0b', C_GREEN = '#10b981', C_GREY = '#9ca3af';
const STATUS_META = {
  notvisited: { label: 'Not visited', color: C_RED },
  behind:     { label: 'Behind',      color: C_AMBER },
  ontrack:    { label: 'On track',    color: C_GREEN },
  notarget:   { label: 'No target',   color: C_GREY },
  visited:    { label: 'Visited',     color: C_GREEN },
};

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function scopedStores() {
  const u = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  if (!u) return null;
  if (u.role === 'admin') return null;
  if (u.role === 'area_manager') {
    return (typeof AREA_STORE_MAP !== 'undefined') ? coverAwareAreaStores(u, AREA_STORE_MAP) : [];   // v136.9: + hand-overs
  }
  if (u.role === 'store_manager') return u.store ? [u.store] : [];
  return null;
}
function allScopedStoreNames() {
  const scope = scopedStores();
  if (scope) return scope.slice();
  return (typeof ALL_STORES !== 'undefined' ? ALL_STORES.slice() : []);
}

// Area filter — HO/admin only (area managers are already locked to one area).
function canPickArea() {
  return scopedStores() === null && typeof AREA_STORE_MAP !== 'undefined' && Object.keys(AREA_STORE_MAP).length > 1;
}
function areaOptions() {
  if (typeof AREA_STORE_MAP === 'undefined') return [];
  return Object.keys(AREA_STORE_MAP).sort(function (a, b) { return String(a).localeCompare(String(b)); });
}
// Label an area id by its active area MANAGER's name (falls back to the area
// name, then "Area <id>") — never shows the raw number. v124.0.
function amLabel(areaId) {
  var nm = (typeof getActiveAreaManagerName === 'function') ? getActiveAreaManagerName(areaId) : '';
  if (nm && nm !== 'Unassigned') return nm;
  var an = (typeof getAreaNameById === 'function') ? getAreaNameById(areaId) : '';
  return an || ('Area ' + areaId);
}
function effectiveStores() {
  if (_area && typeof AREA_STORE_MAP !== 'undefined' && AREA_STORE_MAP[_area]) return AREA_STORE_MAP[_area].slice();
  return allScopedStoreNames();
}

// ── Dates (all Asia/Dubai) ────────────────────────────────────────────────
function dubaiMonth(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' }).slice(0, 7); }
  catch (e) { return String(iso).slice(0, 7); }
}
function currentDubaiMonth() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' }).slice(0, 7);
}
function monthLabel(ym) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const p = ym.split('-'); const y = +p[0], m = +p[1];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });
}
function dubaiDateStr(iso) {           // 'YYYY-MM-DD' in Dubai
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }); }
  catch (e) { return String(iso).slice(0, 10); }
}
function dubaiDateLabel(ymd) {
  if (!ymd) return '';
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (e) { return ymd; }
}
function addDays(ymd, n) {
  const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}
function weekStartOf(ymd) {             // Monday of that week
  const d = new Date(ymd + 'T00:00:00Z');
  const dow = d.getUTCDay();            // 0=Sun..6=Sat
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}
function currentWeekStart() { return weekStartOf(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' })); }
function lastNWeeks(n) {                 // newest first
  const out = []; let d = currentWeekStart();
  for (let i = 0; i < n; i++) { out.push(d); d = addDays(d, -7); }
  return out;
}
function weekRangeLabel(start) {
  const e = addDays(start, 6);
  const sD = new Date(start + 'T00:00:00Z'), eD = new Date(e + 'T00:00:00Z');
  const sd = sD.getUTCDate(), ed = eD.getUTCDate();
  const sm = sD.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short' });
  const em = eD.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short' });
  return sm === em ? (sd + '–' + ed + ' ' + sm) : (sd + ' ' + sm + ' – ' + ed + ' ' + em);
}
function periodLabel() { return _gran === 'week' ? weekRangeLabel(_week) : monthLabel(_month); }

// ── Data ────────────────────────────────────────────────────────────────
let _visits = [];
let _targets = {};   // { store: monthly_target }
let _month = currentDubaiMonth();
let _week = currentWeekStart();
let _gran = 'month'; // 'month' | 'week'
let _area = '';
let _slide = false;  // compact slide view for screenshots
let _loaded = false;

async function fetchAll() {
  const headers = { 'Authorization': 'Bearer ' + getToken() };
  const r = await Promise.all([
    fetch(getWorkerUrl() + '/api/store-visits', { cache: 'no-store', headers }),
    fetch(getWorkerUrl() + '/api/store-visit-targets', { cache: 'no-store', headers }),
  ]);
  if (!r[0].ok) throw new Error('visits ' + r[0].status);
  if (!r[1].ok) throw new Error('targets ' + r[1].status);
  _visits = await r[0].json();
  const targets = await r[1].json();
  _targets = {};
  (targets || []).forEach(function (t) { _targets[t.store] = t.monthly_target; });
  _loaded = true;
}

function isDone(v) { return v && (v.status === 'submitted' || v.status === 'reviewed'); }

function computeRows() {
  const stores = effectiveStores();
  if (_gran === 'week') {
    const start = _week, end = addDays(_week, 6);
    const doneByStore = {}, lastEver = {};
    (_visits || []).forEach(function (v) {
      if (!isDone(v) || !v.store) return;
      const d = dubaiDateStr(v.entry_time || v.created_at);
      if (!lastEver[v.store] || d > lastEver[v.store]) lastEver[v.store] = d;
      if (d >= start && d <= end) doneByStore[v.store] = (doneByStore[v.store] || 0) + 1;
    });
    const rows = stores.map(function (store) {
      const done = doneByStore[store] || 0;
      return { store: store, done: done, status: done > 0 ? 'visited' : 'notvisited', lastEver: lastEver[store] || null };
    });
    rows.sort(function (a, b) {
      const o = { notvisited: 0, visited: 1 };
      return (o[a.status] - o[b.status]) || String(a.store).localeCompare(String(b.store));
    });
    return rows;
  }
  // month
  const doneByStore = {};
  (_visits || []).forEach(function (v) {
    if (!isDone(v) || !v.store) return;
    if (dubaiMonth(v.entry_time || v.created_at) !== _month) return;
    doneByStore[v.store] = (doneByStore[v.store] || 0) + 1;
  });
  const rows = stores.map(function (store) {
    const target = _targets[store] != null ? _targets[store] : 0;
    const done = doneByStore[store] || 0;
    let status;
    if (target === 0) status = 'notarget';
    else if (done === 0) status = 'notvisited';
    else if (done < target) status = 'behind';
    else status = 'ontrack';
    return { store: store, target: target, done: done, status: status };
  });
  const order = { notvisited: 0, behind: 1, ontrack: 2, notarget: 3 };
  rows.sort(function (a, b) { return (order[a.status] - order[b.status]) || String(a.store).localeCompare(String(b.store)); });
  return rows;
}

// ── UI bits ───────────────────────────────────────────────────────────────
function statusPill(status) {
  const m = STATUS_META[status] || { label: status, color: C_GREY };
  return '<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;' +
    'font-weight:700;color:#fff;background:' + m.color + ';white-space:nowrap;">' + escapeHtml(m.label) + '</span>';
}
function progressBar(done, target, color) {
  const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
  return '<div style="background:var(--surface-3);border-radius:6px;height:8px;width:120px;overflow:hidden;display:inline-block;vertical-align:middle;">' +
    '<div style="height:100%;width:' + pct + '%;background:' + color + ';"></div></div>';
}
function statCard(value, label, color) {
  return '<div style="flex:1;min-width:120px;background:var(--card);border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:13px 15px;">' +
    '<div style="font-size:24px;font-weight:800;color:' + (color || 'var(--text,#111)') + ';line-height:1;">' + value + '</div>' +
    '<div style="font-size:11px;color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.4px;margin-top:5px;">' + escapeHtml(label) + '</div></div>';
}

function summaryCards(rows) {
  if (_gran === 'week') {
    const visited = rows.filter(function (r) { return r.status === 'visited'; }).length;
    const notVisited = rows.filter(function (r) { return r.status === 'notvisited'; }).length;
    const totalVisits = rows.reduce(function (s, r) { return s + r.done; }, 0);
    return '<div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:18px;">' +
      statCard(visited + ' / ' + rows.length, 'Stores visited this week', null) +
      statCard(String(notVisited), 'Not visited', notVisited ? C_RED : null) +
      statCard(String(totalVisits), 'Total visits', null) + '</div>';
  }
  const expected = rows.filter(function (r) { return r.target > 0; });
  const totalTarget = expected.reduce(function (s, r) { return s + r.target; }, 0);
  const totalDone = expected.reduce(function (s, r) { return s + r.done; }, 0);
  const visited = expected.filter(function (r) { return r.done > 0; }).length;
  const behind = rows.filter(function (r) { return r.status === 'behind'; }).length;
  const notVisited = rows.filter(function (r) { return r.status === 'notvisited'; }).length;
  return '<div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:18px;">' +
    statCard(totalDone + ' / ' + totalTarget, 'Visits this month', null) +
    statCard(visited + ' / ' + expected.length, 'Stores visited', null) +
    statCard(String(behind), 'Behind target', behind ? C_AMBER : null) +
    statCard(String(notVisited), 'Not visited', notVisited ? C_RED : null) + '</div>';
}

function gapCallout(rows) {
  const gaps = rows.filter(function (r) { return r.status === 'notvisited'; });
  if (!gaps.length) return '';
  return '<div style="background:var(--danger-bg);border:1px solid var(--learn-bad-line);border-radius:10px;padding:12px 14px;margin-bottom:18px;">' +
    '<div style="font-weight:700;font-size:13px;color:var(--danger-deep);margin-bottom:5px;">Not visited in ' + escapeHtml(periodLabel()) + ' (' + gaps.length + ')</div>' +
    '<div style="font-size:13px;color:var(--danger-deep);line-height:1.5;">' + gaps.map(function (r) { return escapeHtml(r.store); }).join(' · ') + '</div></div>';
}

function tableHtml(rows) {
  if (!rows.length) return '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">No stores in scope.</div>';
  if (_gran === 'week') {
    const body = rows.map(function (r) {
      return '<tr>' +
        '<td style="font-weight:600;">' + escapeHtml(r.store) + '</td>' +
        '<td>' + r.done + '</td>' +
        '<td style="color:var(--sub,#6b7280);white-space:nowrap;">' + escapeHtml(r.lastEver ? dubaiDateLabel(r.lastEver) : '—') + '</td>' +
        '<td>' + statusPill(r.status) + '</td></tr>';
    }).join('');
    return '<table class="data-table" style="width:100%;">' +
      '<thead><tr><th>Store</th><th>Visits this week</th><th>Last visit</th><th>Status</th></tr></thead>' +
      '<tbody>' + body + '</tbody></table>';
  }
  const body = rows.map(function (r) {
    const color = STATUS_META[r.status].color;
    return '<tr>' +
      '<td style="font-weight:600;">' + escapeHtml(r.store) + '</td>' +
      '<td>' + (r.target || '—') + '</td>' +
      '<td>' + r.done + '</td>' +
      '<td>' + (r.target > 0 ? progressBar(r.done, r.target, color) : '<span style="color:var(--sub,#9ca3af);">—</span>') + '</td>' +
      '<td>' + statusPill(r.status) + '</td></tr>';
  }).join('');
  return '<table class="data-table" style="width:100%;">' +
    '<thead><tr><th>Store</th><th>Target</th><th>Done</th><th>Progress</th><th>Status</th></tr></thead>' +
    '<tbody>' + body + '</tbody></table>';
}

function ctrlLabel(text, inner) {
  return '<label style="display:flex;flex-direction:column;gap:3px;font-size:11px;font-weight:700;' +
    'color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.4px;">' + text + inner + '</label>';
}
function selStyle(min) { return 'padding:6px 8px;border:1px solid var(--border,#d1d5db);border-radius:7px;font-size:13px;' + (min ? 'min-width:' + min + ';' : ''); }

function filterBar() {
  let html = '<div style="display:flex;align-items:flex-end;gap:12px;margin-bottom:16px;flex-wrap:wrap;">';

  // granularity toggle
  html += ctrlLabel('View',
    '<span style="display:inline-flex;gap:6px;">' +
    '<button id="svr-cadence-gran-month" class="btn ' + (_gran === 'month' ? 'btn-green' : 'btn-outline') + '" style="padding:6px 12px;font-size:13px;">Month</button>' +
    '<button id="svr-cadence-gran-week" class="btn ' + (_gran === 'week' ? 'btn-green' : 'btn-outline') + '" style="padding:6px 12px;font-size:13px;">Week</button></span>');

  // period picker
  if (_gran === 'week') {
    html += ctrlLabel('Week',
      '<select id="svr-cadence-week" style="' + selStyle('150px') + '">' +
      lastNWeeks(8).map(function (w) { return '<option value="' + w + '"' + (w === _week ? ' selected' : '') + '>' + escapeHtml(weekRangeLabel(w)) + '</option>'; }).join('') +
      '</select>');
  } else {
    html += ctrlLabel('Month', '<input type="month" id="svr-cadence-month" value="' + escapeHtml(_month) + '" style="' + selStyle() + '">');
  }

  if (canPickArea()) {
    html += ctrlLabel('Area Manager',
      '<select id="svr-cadence-area" style="' + selStyle('150px') + '"><option value="">All area managers</option>' +
      areaOptions().map(function (a) { return '<option value="' + escapeHtml(a) + '"' + (a === _area ? ' selected' : '') + '>' + escapeHtml(amLabel(a)) + '</option>'; }).join('') +
      '</select>');
  }

  html += '<div style="margin-left:auto;display:flex;gap:8px;">' +
    '<button id="svr-cadence-slide" class="btn ' + (_slide ? 'btn-green' : 'btn-outline') + '" style="padding:7px 12px;font-size:13px;">📊 Slide view</button>' +
    '<button id="svr-cadence-export" class="btn btn-outline" style="padding:7px 12px;font-size:13px;">⬇ Excel</button>' +
  '</div>';
  html += '</div>';
  return html;
}

// Minimal top bar for slide view — title left, toggle + export right, no dropdowns
// (set period/area in full view first, then flip to slide for a clean capture).
function slideTopBar(title) {
  return '<div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;flex-wrap:wrap;">' +
    '<div style="font-size:15px;font-weight:800;color:var(--text,#111);">' + title + '</div>' +
    '<div style="margin-left:auto;display:flex;gap:8px;">' +
      '<button id="svr-cadence-slide" class="btn btn-green" style="padding:7px 12px;font-size:13px;">📊 Slide view</button>' +
      '<button id="svr-cadence-export" class="btn btn-outline" style="padding:7px 12px;font-size:13px;">⬇ Excel</button>' +
    '</div></div>';
}

function renderView(root) {
  const rows = computeRows();
  if (_slide) {
    const title = 'Cadence — ' + escapeHtml(periodLabel()) + ' · ' + escapeHtml(_area ? amLabel(_area) : 'All area managers');
    root.innerHTML = slideTopBar(title) + summaryCards(rows) + gapCallout(rows);
  } else {
    root.innerHTML = filterBar() + summaryCards(rows) + gapCallout(rows) + tableHtml(rows);
  }

  const monthEl = root.querySelector('#svr-cadence-month');
  if (monthEl) monthEl.addEventListener('change', function () { _month = monthEl.value || currentDubaiMonth(); renderView(root); });
  const weekEl = root.querySelector('#svr-cadence-week');
  if (weekEl) weekEl.addEventListener('change', function () { _week = weekEl.value || currentWeekStart(); renderView(root); });
  const areaEl = root.querySelector('#svr-cadence-area');
  if (areaEl) areaEl.addEventListener('change', function () { _area = areaEl.value || ''; renderView(root); });
  const gm = root.querySelector('#svr-cadence-gran-month');
  if (gm) gm.addEventListener('click', function () { _gran = 'month'; renderView(root); });
  const gw = root.querySelector('#svr-cadence-gran-week');
  if (gw) gw.addEventListener('click', function () { _gran = 'week'; renderView(root); });
  const slideBtn = root.querySelector('#svr-cadence-slide');
  if (slideBtn) slideBtn.addEventListener('click', function () { _slide = !_slide; renderView(root); });
  const ex = root.querySelector('#svr-cadence-export');
  if (ex) ex.addEventListener('click', function () { exportCadence(); });
}

// ── Excel export ──────────────────────────────────────────────────────────
function downloadXlsx(sheets, filename) {
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { downloadXlsx(sheets, filename); }, function (e) { alert(e.message); }); return; }
  const wb = XLSX.utils.book_new();
  sheets.forEach(function (s) { XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s.aoa), String(s.name).slice(0, 31)); });
  XLSX.writeFile(wb, filename);
}
function safeTag(s) { return String(s || '').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '') || 'all'; }

function exportCadence() {
  const rows = computeRows();
  let aoa;
  if (_gran === 'week') {
    aoa = [['Store', 'Visits this week', 'Last visit', 'Status']];
    rows.forEach(function (r) { aoa.push([r.store, r.done, r.lastEver || '', STATUS_META[r.status].label]); });
  } else {
    aoa = [['Store', 'Monthly target', 'Done', 'Status']];
    rows.forEach(function (r) { aoa.push([r.store, r.target || 0, r.done, STATUS_META[r.status].label]); });
  }
  const period = _gran === 'week' ? _week : _month;
  downloadXlsx([{ name: 'Cadence', aoa: aoa }], 'ELC_Cadence_' + period + '_' + safeTag(_area) + '.xlsx');
}

async function renderVisitCadence() {
  const root = document.getElementById('visit-cadence-root');
  if (!root) { console.warn('[storeVisitCadence] #visit-cadence-root not found'); return; }
  root.innerHTML = '<div style="padding:28px 4px;color:var(--sub,#6b7280);font-size:14px;">Loading cadence…</div>';
  try { await fetchAll(); renderView(root); }
  catch (e) { root.innerHTML = '<div style="padding:28px 4px;color:var(--ui-bad);font-size:14px;">Couldn\u2019t load cadence. ' + escapeHtml(e.message) + '</div>'; }
}

window.renderVisitCadence = renderVisitCadence;
window.reloadVisitCadence = renderVisitCadence;

export { renderVisitCadence };
